import React from 'react';
import {
  MultiSelect,
  MultiSelectChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { ClipLoader } from 'react-spinners';
import { IVBClaim } from '../../../data-models';
import { colors } from '../../../style';
import { pushAnalyticsDataLayer } from '../../../utils/analytics/helpers';
import { toHTMLElement } from '../../../utils/analytics/kendo-helpers';

interface VBClaimSelectProps {
  claims: IVBClaim[];
  isLoading: boolean;
  selectedClaims: IVBClaim[];
  setSelectedClaims: React.Dispatch<React.SetStateAction<IVBClaim[]>>;
}

export const vbClaimLabel = (claim: IVBClaim) =>
  `${claim.name} - ${claim.id} - (${claim.receivedDate})`;

const Description = () => (
  <div
    style={{
      fontFamily: 'Open Sans',
      color: '#757575',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      paddingBottom: '24px',
    }}
  >
    It looks like your document may be related to one or more of the Voluntary
    Benefit Claims below. Please select any claims you&apos;d like to send your
    document(s) to, or leave them unselected if none apply.
  </div>
);

const VBClaimSelect = ({
  claims,
  isLoading,
  selectedClaims,
  setSelectedClaims,
}: VBClaimSelectProps) => {
  const [opened, setOpened] = React.useState(false);

  if (isLoading) {
    return (
      <div id="vbClaimSelectLoading" style={{ paddingBottom: '24px' }}>
        <ClipLoader size={24} color={colors.blue} />
      </div>
    );
  }

  if (claims.length === 0) {
    return null;
  }

  const data = claims.map((claim) => ({
    ...claim,
    label: vbClaimLabel(claim),
  }));

  const selectedIds = new Set(selectedClaims.map((claim) => claim.id));
  const value = data.filter((item) => selectedIds.has(item.id));

  const onChange = (event: MultiSelectChangeEvent) => {
    setSelectedClaims(
      event.value.map((item: IVBClaim) => ({
        name: item.name,
        id: item.id,
        receivedDate: item.receivedDate,
      })),
    );

    const hostEl = toHTMLElement(event?.target?.element) ?? null;
    pushAnalyticsDataLayer(
      hostEl,
      {
        attribute1: 'upload a document',
        attribute2: 'undefined',
        attribute3: 'upload_interaction',
        attribute4: 'undefined',
        label: 'Voluntary Benefit Claims',
      },
      'vbClaimSelect',
    );
  };

  return (
    <div id="vbClaimSelect" style={{ paddingBottom: '24px' }}>
      <Description />
      <div
        style={{
          fontFamily: 'Open Sans',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          color: '#757575',
          paddingBottom: '8px',
          lineHeight: '16px',
        }}
      >
        Voluntary Benefit Claims
      </div>
      <MultiSelect
        id="vbClaimSelectDropdown"
        style={{ maxWidth: '400px', width: '100%' }}
        data={data}
        value={value}
        onChange={onChange}
        dataItemKey="id"
        textField="label"
        placeholder="none selected"
        autoClose={false}
        opened={opened}
        onOpen={() => setOpened(true)}
        onClose={() => setOpened(false)}
        itemRender={(li, itemProps) =>
          React.cloneElement(
            li,
            li.props,
            <>
              <Checkbox
                id={`vb-claim-option-${itemProps.dataItem.id}`}
                checked={itemProps.selected}
                readOnly
                tabIndex={-1}
              />
              <span className="tw-ml-2">{itemProps.dataItem.label}</span>
            </>,
          )
        }
        footer={
          <div className="tw-flex tw-justify-end tw-gap-2 tw-p-2">
            <Button
              id="vb-claim-clear-button"
              fillMode="flat"
              onClick={() => setSelectedClaims([])}
            >
              Clear
            </Button>
            <Button
              id="vb-claim-select-button"
              themeColor="primary"
              onClick={() => setOpened(false)}
            >
              Select
            </Button>
          </div>
        }
      />
    </div>
  );
};

export default VBClaimSelect;
